import {
  CAPABILITY_MANAGEMENT,
  PERMISSION_DATA,
  ROLE_MANAGEMENT,
} from "api/ApiPath";
import { ActionInjection } from "app/actions/di/ActionInjection";
import { ActionUseCase } from "app/actions/domain/useCases/ActionUseCase";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { IFilterRepository } from "app/filters/domain/repository/IFilterRepository";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { PermissionDataInjection } from "features/permissionDataManagement/di/PermissionDataInjection";
import { injectable } from "tsyringe";
import { DataFormat } from "utils/format/DataFormat";
import { strings } from "utils/localizedStrings";
import { PermissionDataActionEntity } from "../entities/PermissionDataActionEntity";
import { PermissionDataDetailEntity } from "../entities/PermissionDataDetailEntity";
import { PermissionDataRequestEntity } from "../entities/PermissionDataRequestEntity";
import { PermissionDataResponseEntity } from "../entities/PermissionDataResponseEntity";
import { IPermissionDataRepository } from "../repositories/IPermissionDataRepository";

@injectable()
export class PermissionDataUseCase {
  private permissionDataRepo: IPermissionDataRepository;
  private filterRepo: IFilterRepository;
  private filterUseCase: FilterUseCase;
  private actionUseCase: ActionUseCase;

  constructor() {
    this.actionUseCase = ActionInjection.getUseCase();
    this.permissionDataRepo = PermissionDataInjection.getRepo();
    this.filterRepo = FilterInjection.getRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private checkParams(params: PermissionDataRequestEntity) {
    const finalParams = { ...params };
    delete finalParams.created;

    Object.keys(finalParams).forEach((rawKey) => {
      const key = rawKey as keyof PermissionDataRequestEntity;
      if (
        finalParams[key] === strings.GlobalLabels.All ||
        finalParams[key] === "" ||
        typeof finalParams[key] === "undefined"
      ) {
        delete finalParams[key];
      }
    });

    return finalParams;
  }

  private updateBody(
    body: PermissionDataActionEntity,
    detailInfo: PermissionDataDetailEntity
  ) {
    const keysToAvoid = [
      "code",
      "capabilities",
      "roles",
      "users",
      "configuration",
    ];
    const finalCapabilities = !body["code"]
      ? body?.capabilities
      : this.actionUseCase.buildArrayEditActions(
          body?.capabilities as Array<string>,
          detailInfo?.capabilities
        );
    const finalRoles = !body["code"]
      ? body?.roles
      : this.actionUseCase.buildArrayEditActions(
          body?.roles as Array<string>,
          detailInfo?.roles
        );
    const finalUsers = !body["code"]
      ? body?.users
      : this.actionUseCase.buildArrayEditActions(
          body?.users as Array<string>,
          detailInfo?.users
        );
    const afterBody = {
      code: body["code"],
      ...body,
      configuration:
        typeof body?.configuration === "string" && body?.configuration
          ? JSON.parse(DataFormat.autoCorrectJSON(String(body.configuration)))
          : body?.configuration,
      capabilities: finalCapabilities,
      roles: finalRoles,
      users: finalUsers,
    } as typeof body;

    return {
      keysToAvoid,
      afterBody,
    };
  }

  private isRoleMatchedInAction(
    value: PermissionDataActionEntity["roles"],
    currentRoleCode?: string
  ) {
    if (!currentRoleCode || !value) return false;

    if (Array.isArray(value)) {
      return value.includes(currentRoleCode);
    }

    return (
      value?.add?.includes(currentRoleCode) ||
      value?.erase?.includes(currentRoleCode) ||
      false
    );
  }

  private isCurrentRoleInDetail(
    detailInfo: PermissionDataDetailEntity,
    currentRoleCode?: string
  ) {
    if (!currentRoleCode) return false;
    return (
      detailInfo?.roles?.some((item) => item?.code === currentRoleCode) || false
    );
  }

  public shouldRefreshStartupOnUpdate(
    body: PermissionDataActionEntity,
    detailInfo: PermissionDataDetailEntity,
    currentRoleCode?: string
  ) {
    if (!currentRoleCode) return false;

    const updatedBody = this.updateBody(body, detailInfo)?.afterBody;
    const rolesMatched = this.isRoleMatchedInAction(
      updatedBody?.roles,
      currentRoleCode
    );
    const capabilitiesChanged = !!updatedBody?.capabilities;
    const statusChanged =
      typeof updatedBody?.status !== "undefined" &&
      String(updatedBody?.status) !== String(detailInfo?.status);
    const nameChanged =
      !!updatedBody?.name && updatedBody?.name !== detailInfo?.name;
    const pathChanged =
      !!updatedBody?.path && updatedBody?.path !== detailInfo?.path;
    const parentChanged =
      !!updatedBody?.parent && updatedBody?.parent !== detailInfo?.parent?.code;
    const iconChanged =
      !!updatedBody?.icon && updatedBody?.icon !== detailInfo?.icon;
    const currentRoleAlreadyInDetail = this.isCurrentRoleInDetail(
      detailInfo,
      currentRoleCode
    );

    return (
      rolesMatched ||
      parentChanged ||
      nameChanged ||
      statusChanged ||
      pathChanged ||
      iconChanged ||
      (capabilitiesChanged && currentRoleAlreadyInDetail)
    );
  }

  async getList(
    params: PermissionDataRequestEntity
  ): Promise<ListBaseApiResponseModel<PermissionDataResponseEntity>> {
    return await this.permissionDataRepo.getList(this.checkParams(params));
  }

  async getDetail(
    params: PermissionDataRequestEntity
  ): Promise<BaseApiResponseModel<PermissionDataDetailEntity>> {
    return await this.permissionDataRepo.getDetail(this.checkParams(params));
  }

  async createMenu(
    body: PermissionDataActionEntity,
    detailInfo: PermissionDataDetailEntity = {}
  ): Promise<BaseApiResponseModel<PermissionDataResponseEntity> | undefined> {
    const updatedBody = this.updateBody(body, detailInfo);
    return await this.actionUseCase.create({
      path: PERMISSION_DATA.CREATE,
      inputValue: updatedBody?.afterBody,
    });
  }

  async updateMenu(
    body: PermissionDataActionEntity,
    detailInfo: PermissionDataDetailEntity
  ): Promise<BaseApiResponseModel<PermissionDataResponseEntity> | undefined> {
    const updatedBody = this.updateBody(body, detailInfo);
    return await this.actionUseCase.update({
      path: PERMISSION_DATA.UPDATE,
      allValToString: true,
      comparedValue: detailInfo,
      inputValue: updatedBody?.afterBody,
      keysToAvoid: updatedBody?.keysToAvoid,
    });
  }

  async deleteMenu(
    params: PermissionDataRequestEntity
  ): Promise<BaseApiResponseModel<PermissionDataResponseEntity> | undefined> {
    return await this.actionUseCase.delete({
      path: PERMISSION_DATA.DELETE,
      inputValue: params,
    });
  }

  async getCapability(
    params: BaseFilterListRequestModel
  ): Promise<ListBaseApiResponseModel<BaseFilterListResponseModel>> {
    const path = CAPABILITY_MANAGEMENT.FILTER_LIST;
    return await this.filterRepo.getFilterList(path, params);
  }

  async getRole(
    params: BaseFilterListRequestModel
  ): Promise<ListBaseApiResponseModel<BaseFilterListResponseModel>> {
    const path = ROLE_MANAGEMENT.FILTER_LIST;
    return await this.filterRepo.getFilterList(path, params);
  }

  async getParentMenu(
    params: PermissionDataRequestEntity
  ): Promise<ListBaseApiResponseModel<PermissionDataResponseEntity>> {
    const path = PERMISSION_DATA.FILTER_LIST;
    return await this.filterRepo.getFilterList(path, params);
  }

  async getUsers(
    params: BaseUserRequestModel
  ): Promise<{ data: BaseUserResponseModel[]; pagination: any } | undefined> {
    return await this.filterUseCase.fetchListAndPagingWithFeature({
      path: PERMISSION_DATA.USER_LIST,
      isNeedFeature: true,
      params,
    });
  }
}
